import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { expensifyService } from '@/services/integrations/expensifyService';
import { hiveService } from '@/services/integrations/hiveService';
import { googleFormsService } from '@/services/integrations/googleFormsService';
import { supabase } from '@/integrations/supabase/client';
import {
  Receipt,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react';

export default function IntegrationSettings() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('expensify');
  const [organizationId, setOrganizationId] = useState<string>('');

  // Expensify config state
  const [expensifyConfig, setExpensifyConfig] = useState({
    partner_user_id: '',
    partner_user_secret: '',
    auto_import: false,
    auto_approve_threshold: 500,
  });
  const [expensifyConnected, setExpensifyConnected] = useState(false);

  // HIVE config state
  const [hiveConfig, setHiveConfig] = useState({
    api_key: '',
    workspace_id: '',
    auto_import_time: false,
    auto_create_projects: false,
  });
  const [hiveConnected, setHiveConnected] = useState(false);

  // Google Forms config state
  const [googleFormsConfig, setGoogleFormsConfig] = useState({
    service_account_email: '',
    private_key: '',
    response_sheet_id: '',
    auto_import_enabled: false,
    import_frequency_hours: 24,
  });
  const [googleFormsConnected, setGoogleFormsConnected] = useState(false);

  // Load organization and configurations on mount
  useEffect(() => {
    loadConfigurations();
  }, [user]);

  const loadConfigurations = async () => {
    if (!user) return;

    try {
      // Get user's organization
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (profile?.organization_id) {
        setOrganizationId(profile.organization_id);
        
        // Load existing configurations
        await loadExpensifyConfig(profile.organization_id);
        await loadHiveConfig(profile.organization_id);
        await loadGoogleFormsConfig(profile.organization_id);
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
    }
  };

  const loadExpensifyConfig = async (orgId: string) => {
    try {
      const { data } = await supabase
        .from('integration_configs')
        .select('config_data')
        .eq('organization_id', orgId)
        .eq('integration_type', 'expensify')
        .single();

      if (data?.config_data) {
        setExpensifyConfig(data.config_data);
        setExpensifyConnected(true);
      }
    } catch (error) {
      // No config found
    }
  };

  const loadHiveConfig = async (orgId: string) => {
    try {
      const { data } = await supabase
        .from('integration_configs')
        .select('config_data')
        .eq('organization_id', orgId)
        .eq('integration_type', 'hive')
        .single();

      if (data?.config_data) {
        setHiveConfig(data.config_data);
        setHiveConnected(true);
      }
    } catch (error) {
      // No config found
    }
  };

  const loadGoogleFormsConfig = async (orgId: string) => {
    try {
      const { data } = await supabase
        .from('integration_configs')
        .select('config_data')
        .eq('organization_id', orgId)
        .eq('integration_type', 'google_forms')
        .single();

      if (data?.config_data) {
        setGoogleFormsConfig(data.config_data);
        setGoogleFormsConnected(true);
      }
    } catch (error) {
      // No config found
    }
  };

  // Save Expensify configuration
  const saveExpensifyConfig = async () => {
    setLoading(true);
    try {
      // Test connection first
      await expensifyService.initialize(organizationId);
      const testResult = await expensifyService.testConnection();
      
      if (!testResult.success) {
        throw new Error(testResult.error || 'Failed to connect to Expensify');
      }

      // Save configuration
      const { error } = await supabase
        .from('integration_configs')
        .upsert({
          organization_id: organizationId,
          integration_type: 'expensify',
          config_data: expensifyConfig,
          is_active: true,
        });

      if (error) throw error;

      setExpensifyConnected(true);
      toast({
        title: 'Success',
        description: 'Expensify integration configured successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save Expensify configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Save HIVE configuration
  const saveHiveConfig = async () => {
    setLoading(true);
    try {
      // Test connection first
      await hiveService.initialize(organizationId);
      const testResult = await hiveService.testConnection();
      
      if (!testResult.success) {
        throw new Error(testResult.error || 'Failed to connect to HIVE');
      }

      // Save configuration
      const { error } = await supabase
        .from('integration_configs')
        .upsert({
          organization_id: organizationId,
          integration_type: 'hive',
          config_data: hiveConfig,
          is_active: true,
        });

      if (error) throw error;

      setHiveConnected(true);
      toast({
        title: 'Success',
        description: 'HIVE integration configured successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save HIVE configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Save Google Forms configuration
  const saveGoogleFormsConfig = async () => {
    setLoading(true);
    try {
      // Save configuration
      await googleFormsService.saveConfig(organizationId, googleFormsConfig);
      
      // Test connection
      const testResult = await googleFormsService.testConnection();
      
      if (!testResult.success) {
        throw new Error(testResult.error || 'Failed to connect to Google Forms');
      }

      setGoogleFormsConnected(true);
      toast({
        title: 'Success',
        description: 'Google Forms integration configured successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save Google Forms configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Integration Settings</h1>
        <p className="text-gray-600">Configure third-party integrations for enhanced functionality</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="expensify" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Expensify
          </TabsTrigger>
          <TabsTrigger value="hive" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            HIVE
          </TabsTrigger>
          <TabsTrigger value="googleforms" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Google Forms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expensify" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Expensify Integration
                  </CardTitle>
                  <CardDescription>
                    Connect Expensify to automatically sync expense reports and receipts
                  </CardDescription>
                </div>
                {expensifyConnected ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <XCircle className="h-5 w-5" />
                    Not Connected
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="partner-user-id">Partner User ID</Label>
                <Input
                  id="partner-user-id"
                  type="text"
                  value={expensifyConfig.partner_user_id}
                  onChange={(e) => setExpensifyConfig({ ...expensifyConfig, partner_user_id: e.target.value })}
                  placeholder="Enter your Expensify Partner User ID"
                />
              </div>

              <div>
                <Label htmlFor="partner-user-secret">Partner User Secret</Label>
                <Input
                  id="partner-user-secret"
                  type="password"
                  value={expensifyConfig.partner_user_secret}
                  onChange={(e) => setExpensifyConfig({ ...expensifyConfig, partner_user_secret: e.target.value })}
                  placeholder="Enter your Expensify Partner User Secret"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-import">Auto Import Expenses</Label>
                  <p className="text-sm text-gray-600">Automatically import new expense reports</p>
                </div>
                <Switch
                  id="auto-import"
                  checked={expensifyConfig.auto_import}
                  onCheckedChange={(checked) => setExpensifyConfig({ ...expensifyConfig, auto_import: checked })}
                />
              </div>

              <div>
                <Label htmlFor="auto-approve-threshold">Auto-Approve Threshold ($)</Label>
                <Input
                  id="auto-approve-threshold"
                  type="number"
                  value={expensifyConfig.auto_approve_threshold}
                  onChange={(e) => setExpensifyConfig({ ...expensifyConfig, auto_approve_threshold: Number(e.target.value) })}
                  placeholder="500"
                />
                <p className="text-sm text-gray-600 mt-1">Expenses below this amount will be auto-approved</p>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={saveExpensifyConfig} 
                  disabled={loading || !expensifyConfig.partner_user_id || !expensifyConfig.partner_user_secret}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Configuration
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open('https://www.expensify.com/tools/integrations/', '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Expensify Docs
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hive" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    HIVE Time Tracking
                  </CardTitle>
                  <CardDescription>
                    Connect HIVE to sync time entries and project tracking
                  </CardDescription>
                </div>
                {hiveConnected ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <XCircle className="h-5 w-5" />
                    Not Connected
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="hive-api-key">API Key</Label>
                <Input
                  id="hive-api-key"
                  type="password"
                  value={hiveConfig.api_key}
                  onChange={(e) => setHiveConfig({ ...hiveConfig, api_key: e.target.value })}
                  placeholder="Enter your HIVE API key"
                />
              </div>

              <div>
                <Label htmlFor="hive-workspace-id">Workspace ID</Label>
                <Input
                  id="hive-workspace-id"
                  type="text"
                  value={hiveConfig.workspace_id}
                  onChange={(e) => setHiveConfig({ ...hiveConfig, workspace_id: e.target.value })}
                  placeholder="Enter your HIVE Workspace ID"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-import-time">Auto Import Time Entries</Label>
                  <p className="text-sm text-gray-600">Automatically sync time entries from HIVE</p>
                </div>
                <Switch
                  id="auto-import-time"
                  checked={hiveConfig.auto_import_time}
                  onCheckedChange={(checked) => setHiveConfig({ ...hiveConfig, auto_import_time: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-create-projects">Auto Create Projects</Label>
                  <p className="text-sm text-gray-600">Create HIVE projects when CRM projects are created</p>
                </div>
                <Switch
                  id="auto-create-projects"
                  checked={hiveConfig.auto_create_projects}
                  onCheckedChange={(checked) => setHiveConfig({ ...hiveConfig, auto_create_projects: checked })}
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={saveHiveConfig} 
                  disabled={loading || !hiveConfig.api_key || !hiveConfig.workspace_id}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Configuration
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open('https://app.hive.com/api-docs', '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  HIVE API Docs
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="googleforms" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Google Forms Integration
                  </CardTitle>
                  <CardDescription>
                    Connect Google Forms to automate gift feedback surveys
                  </CardDescription>
                </div>
                {googleFormsConnected ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <XCircle className="h-5 w-5" />
                    Not Connected
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="service-account-email">Service Account Email</Label>
                <Input
                  id="service-account-email"
                  type="email"
                  value={googleFormsConfig.service_account_email}
                  onChange={(e) => setGoogleFormsConfig({ ...googleFormsConfig, service_account_email: e.target.value })}
                  placeholder="your-service-account@project.iam.gserviceaccount.com"
                />
              </div>

              <div>
                <Label htmlFor="private-key">Private Key (JSON)</Label>
                <textarea
                  id="private-key"
                  className="w-full min-h-[100px] p-2 border rounded-md font-mono text-sm"
                  value={googleFormsConfig.private_key}
                  onChange={(e) => setGoogleFormsConfig({ ...googleFormsConfig, private_key: e.target.value })}
                  placeholder='{"type": "service_account", ...}'
                />
              </div>

              <div>
                <Label htmlFor="response-sheet-id">Response Sheet ID (Optional)</Label>
                <Input
                  id="response-sheet-id"
                  type="text"
                  value={googleFormsConfig.response_sheet_id}
                  onChange={(e) => setGoogleFormsConfig({ ...googleFormsConfig, response_sheet_id: e.target.value })}
                  placeholder="Google Sheets ID for storing responses"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-import-responses">Auto Import Responses</Label>
                  <p className="text-sm text-gray-600">Automatically import survey responses</p>
                </div>
                <Switch
                  id="auto-import-responses"
                  checked={googleFormsConfig.auto_import_enabled}
                  onCheckedChange={(checked) => setGoogleFormsConfig({ ...googleFormsConfig, auto_import_enabled: checked })}
                />
              </div>

              <div>
                <Label htmlFor="import-frequency">Import Frequency (hours)</Label>
                <Input
                  id="import-frequency"
                  type="number"
                  value={googleFormsConfig.import_frequency_hours}
                  onChange={(e) => setGoogleFormsConfig({ ...googleFormsConfig, import_frequency_hours: Number(e.target.value) })}
                  placeholder="24"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={saveGoogleFormsConfig} 
                  disabled={loading || !googleFormsConfig.service_account_email || !googleFormsConfig.private_key}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Configuration
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open('https://developers.google.com/forms/api/quickstart', '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Google Forms API Docs
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}